import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { StepLayout } from "../components/StepLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  getMerchantStepPath,
  PROVIDER_TYPE,
  getResumeStep,
} from "../constants";
import { useApplicationId } from "../hooks/use-application-id";
import { useAuth } from "@/providers/auth";
import { useApplicationInfo } from "@/queries/provider-onboarding/use-application-info";
import { APPLICATION_STATUS } from "@/queries/provider-onboarding/application-status";
import { useBeginIdv } from "@/queries/provider-onboarding/use-idv";
import { isOtpFlow } from "@/queries/provider-onboarding/types";
import { toast } from "react-toastify";

const IDV_STATUSES = new Set([
  APPLICATION_STATUS.APPLICATION_CREATED,
  APPLICATION_STATUS.IDENTITY_VERIFICATION,
  APPLICATION_STATUS.IDENTITY_VERIFICATION_SUBMITTED,
]);

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)})-${digits.slice(3)}`;
  return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const isPhoneComplete = (value: string) =>
  value.replace(/\D/g, "").length === 10;

const Step2 = () => {
  const history = useHistory();
  const applicationId = useApplicationId();
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { cognitoId, clientId, userRole } = useAuth();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;

  const { data: applicationInfo, isLoading: isCheckingStatus } =
    useApplicationInfo(userId!, PROVIDER_TYPE);

  useEffect(() => {
    if (isCheckingStatus || !applicationInfo) return;
    if (!applicationInfo.external_application_id) {
      history.replace(getMerchantStepPath(1));
      return;
    }
    if (!IDV_STATUSES.has(applicationInfo.status as any)) {
      history.replace(
        getResumeStep(
          applicationInfo.status,
          applicationInfo.external_application_id,
        ),
      );
    }
  }, [applicationInfo, isCheckingStatus, history]);

  const { mutate: beginIdvMutation } = useBeginIdv({
    onSuccess: (response) => {
      history.push(getMerchantStepPath(3, applicationId), {
        sessionId: response.sessionId,
        questions: response.questions,
        otp: isOtpFlow(response),
      });
    },
    onError: (err) => {
      if (
        import.meta.env.MODE !== 'production'
      ) {
        console.error(
          "[Step2] beginIdv error, skipping to step 4 in dev:",
          err,
        );
        history.push(getMerchantStepPath(4, applicationId));
      } else {
        setIsSubmitting(false);
        toast.error("Failed to start identity verification. Please try again.");
      }
    },
  });

  const handleNext = () => {
    if (!isPhoneComplete(phone)) return;
    const digits = phone.replace(/\D/g, "");
    console.log("[Step2] submitting phone for IDV:", digits);
    setIsSubmitting(true);
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then(({ ip }: { ip: string }) => {
        beginIdvMutation({
          userId: userId || "",
          applicationId,
          providerType: PROVIDER_TYPE,
          payload: { ip_address: ip, phone_number: digits },
        });
      })
      .catch(() => {
        setIsSubmitting(false);
        toast.error("Failed to retrieve network info. Please try again.");
      });
  };

  return (
    <StepLayout
      step={2}
      title="Identity Verification"
      onNext={handleNext}
      nextDisabled={!isPhoneComplete(phone) || isSubmitting}
      isLoading={isSubmitting}
      hideBack
    >
      <p className="text-sm text-gray-600 leading-relaxed -mt-2">
        To continue your application, we need to verify your identity. Please
        enter your phone number to begin. You may receive a text message with a
        code to enter, or in some cases be required to answer some additional
        questions to verify your identity.
      </p>

      <div className="space-y-1.5">
        <Label>Enter your phone number</Label>
        <Input
          type="text"
          inputMode="tel"
          value={phone}
          onChange={(e) => setPhone(formatPhone(e.target.value))}
          placeholder="(123)-123-1234"
          disabled={isSubmitting}
        />
      </div>
      <p className="text-sm text-gray-400">
        Having trouble?{" "}
        <button
          type="button"
          onClick={() => history.push(getMerchantStepPath(4, applicationId))}
          className="underline hover:text-gray-600"
        >
          Skip this step.
        </button>
      </p>
    </StepLayout>
  );
};

export default Step2;
